import { DetailFilter } from '@components/detailFilter';
import { LiveFilter } from '@components/liveFilter';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useFragment } from 'react-relay';
import { aboTableFragment } from './AboTable.relay';
import { AboTableHeader } from './lib/aboHeader';
import { AboTranche } from './lib/aboTranche';
import { useDocumentSubscription, useResponsiveWidth, useTableFiltering } from './lib/hooks';
import { LanguagePreference } from './lib/languagePreference';
import type { AboTablePropsType } from './types';

const EmptyMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="p-2.5 text-center">{children}</div>;

const AboTable = forwardRef<any, AboTablePropsType>((props, ref) => {
  const table = useFragment(aboTableFragment, props.table);
  const documentKeys = useMemo(() => table.documentlabels.labels.map((l) => l.key), [table.documentlabels.labels]);

  const { containerRef, excludedColsInOtherDocs, adjustWidth } = useResponsiveWidth();

  const { documentsSubscribed, headerDocumentsSubscribed, preferredLanguage, changeDocumentsSubscription, changePreferredLanguage, syncFromRelay } = useDocumentSubscription({
    entries: table.entries as any,
    documentHeaders: table.documentheaders,
    documentKeys,
    currentLanguageSelection: table.languagepreference.currentselection || 'DE',
  });

  const { sortInfo, liveFilterInfo, detailFilterInfo, subscriptionFilterInfo, detailFilterVisible, filteredResults, handleSort, liveFilterHandler, detailFilterHandler, subscriptionFilterHandler, toggleDetailFilter } =
    useTableFiltering(table.entries as any, documentsSubscribed, excludedColsInOtherDocs);

  useImperativeHandle(
    ref,
    () => ({
      refs: {
        component: {
          state: {
            documentssubscribed: documentsSubscribed,
            headerDocumentsSubscribed,
            preferredlanguage: preferredLanguage,
          },
        },
      },
    }),
    [documentsSubscribed, headerDocumentsSubscribed, preferredLanguage],
  );

  // Sync document subscription state when relay data changes
  useEffect(() => {
    adjustWidth();
    syncFromRelay(table.entries as any);
  }, [adjustWidth, syncFromRelay, table.entries]);

  const { results, count: filteredCounter } = filteredResults;

  return (
    <div
      ref={containerRef}
      className="Widget-container Base-container Subscription-table-container relative mx-auto flex w-full max-w-[820px] flex-wrap items-start justify-between pb-[50px] max-[1100px]:m-0 max-[1100px]:block max-[1100px]:w-full max-[1100px]:max-w-full max-[968px]:pb-2.5!"
    >
      <div className="w-full">
        <div className="flex flex-col tablet:flex-row tablet:items-center w-full tablet:gap-4 items-start justify-start border-b border-[#d0d0d0] pb-5">
          <LiveFilter
            liveFilter={table.livefilter}
            filterHandlerFn={liveFilterHandler}
            filterText={Object.values(liveFilterInfo)[0]?.[0] || ''}
            className="w-full min-w-[34%] rounded-[20px] border border-[#d0d0d0] bg-[url(/static/front/assets/other-icons/search.png)] bg-position-[right_8px_center] bg-no-repeat px-[15px] py-[5px] pr-[35px] text-sm focus-within:border-[#a0a0a0] focus-within:outline-none max-[620px]:mb-2.5"
            filterCols={['fundsname', 'isin']}
          />
          <button type="button" onClick={toggleDetailFilter} className="relative z-10 min-w-max cursor-pointer text-left text-sm text-accent no-underline max-[620px]:mt-5 max-[620px]:flex max-[620px]:p-0">
            {detailFilterVisible ? table.filterbuttontextexpanded : table.filterbuttontext}
            <span
              aria-hidden={true}
              className={`absolute right-0 top-0 h-5 w-[25px] bg-[url(/static/front/assets/chevron/_chevron-down.svg)] bg-position-[right_5px_center] bg-no-repeat transition-transform max-[620px]:relative max-[620px]:inline-block ${detailFilterVisible ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
        <DetailFilter
          isVisible={detailFilterVisible}
          detailFilter={table.detailfilter}
          filterHandlerFn={detailFilterHandler}
          resetHandlerFn={(cols: string | null | undefined, vals: string[]) => {
            detailFilterHandler(cols, vals);
            subscriptionFilterHandler(cols, vals);
          }}
          filterVals={detailFilterInfo}
          filteredCounter={filteredCounter}
          radio={false}
          hasResetOption={true}
        />
        <DetailFilter isVisible={true} detailFilter={table.subscriptionfilter} filterHandlerFn={subscriptionFilterHandler} filterVals={subscriptionFilterInfo} radio={true} filteredCounter={0} hasResetOption={false} />
        <LanguagePreference component={table.languagepreference} selected={preferredLanguage} preferredLanguageHandler={changePreferredLanguage} />
      </div>
      <table className="Table Subscription-table mt-20 mb-10 w-full max-w-[1000px] [&&_tbody:last-child_tr:last-child_td]:border-b-0 max-[1100px]:max-w-[565px] max-[1100px]:[&&_thead]:block max-[1100px]:[&&_thead]:border-b max-[1100px]:[&&_thead]:border-accent max-[1100px]:[&&_thead]:px-2.5 max-[1100px]:[&&_thead_tr]:flex max-[1100px]:[&&_thead_tr]:items-center max-[1100px]:[&&_thead_tr]:justify-between max-[1100px]:[&&_thead_th]:border-b-0 max-[1100px]:[&&_thead_td]:border-b-0 max-[1100px]:[&&_tbody]:block max-[1100px]:[&&_tbody:first-of-type]:mt-2.5 max-[968px]:mb-0 max-[968px]:mt-10 max-[620px]:max-w-[calc(100vw-40px)]">
        <AboTableHeader
          header={table}
          sortHandler={handleSort}
          sortInfo={sortInfo}
          checkFn={changeDocumentsSubscription}
          checkState={documentsSubscribed}
          documentLabels={table.documentlabels as any}
          excludedColsInOtherDocs={excludedColsInOtherDocs}
        />
        <tbody>
          {results.map((tranche) => (
            <AboTranche
              key={tranche.id}
              allDocumentsChecked={documentsSubscribed[tranche.isin]}
              id={tranche.isin}
              tranche={tranche}
              checkFn={changeDocumentsSubscription}
              documentLabels={table.documentlabels as any}
              excludedColsInOtherDocs={excludedColsInOtherDocs}
            />
          ))}
        </tbody>
        {results.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={5}>
                <EmptyMessage>{table.noResultsMessage}</EmptyMessage>
              </td>
            </tr>
          </tbody>
        ) : null}
      </table>
    </div>
  );
});

export { AboTable };
